<template>
  <form @submit.prevent="handleConversionUpdate">
    <AddProjectFormInput
      id="project-currency"
      disabled
      :value="projectRatesInfo"
      :label-text="$t('Project currency')"
    />

    <div class="three-columns-form-row">
      <AddProjectFormInput
        v-for="(rate, key) in currency_rates_info"
        :id="`conversion-${key}`"
        :key="key"
        v-model="currency_rates[key]"
        :step="'0.0001'"
        :label-text="`${currencyWithSymbol(key)} → ${projectCurrency}`"
        :type="'number'"
      >
        <template #pre> {{ projectCurrencySymbol }}&nbsp; </template>
        <template #post>
          <InfoTooltip top container-class="flex-end">
            <template #element>
              <Icon
                type="info-circle"
                size="18"
                :color="
                  rate.actual_rate_difference_percent && rate.actual_rate_difference_percent > 5
                    ? 'warning'
                    : 'secondary'
                "
              />
            </template>
            <div class="text-sm" style="text-align: left">
              {{ $t('Conversion rate was set on') }} {{ prettyRateDate }} <br />
              {{ $t('Today the exchange rate is') }} {{ rate.actual_rate }}
              {{ settings.project.currency }} {{ $t('per') }} 1
              {{ key }}
              <strong v-if="rate.actual_rate_difference_percent">
                ({{ rate.actual_rate_difference_percent }}% difference)
              </strong>
              <br /><br />
              {{ $t('Used by') }}: <br />
              <span v-for="(team, key) in rate.teams" :key="key">
                – {{ team.name }} {{ $t('in') }}
                {{ team.tenders.map((tender) => tender.name).join(', ') }}<br />
              </span>
            </div>
          </InfoTooltip>
        </template>
      </AddProjectFormInput>
    </div>
    <Button action-type="submit">
      {{ $t('Update conversion rates') }}
    </Button>
  </form>
</template>

<script>
  import AddProjectFormInput from '@/components/ui/FormInput';
  import InfoTooltip from '@/components/ui/InfoTooltip';
  import prettyDates from '@/utils/formatting/pretty-date';
  import { mapActions, mapGetters, mapState } from 'vuex';
  import RatesUpdate from '@/containers/project/RatesUpdate';
  import { currentDate } from '@/utils/date/current-date';
  import Button from '@/components/ui-v2/Button';
  import Icon from '@/components/ui/Icon';
  import factory from '@/utils/api/factory';
  import parseError from '@/utils/formatting/parse-error';

  export default {
    name: 'ProjectCurrencyForm',
    components: {
      AddProjectFormInput,
      InfoTooltip,
      Button,
      Icon,
    },
    props: {
      settings: {
        type: Object,
        default: () => ({
          id: [String, Number],
          contract_number: String,
          client_company_name: String,
          client_company_director: String,
          user_company_name: String,
          user_company_director: String,
          project: {
            id: Number,
            type: Object,
            default: () => ({
              name: String,
              address: String,
            }),
          },
        }),
      },
      createForm: Boolean,
    },
    computed: {
      ...mapGetters('global', ['currencyWithSymbol', 'currencySymbol']),
      ...mapState('confirmModal', ['context']),
      ...mapState('global', ['currency']),
      projectCurrency() {
        return this.currencyWithSymbol(this.settings.project.currency);
      },
      projectRatesInfo() {
        return `${this.projectCurrency} · ${this.$t('Rates from')} ${this.prettyRateDate}`;
      },
      projectCurrencySymbol() {
        return this.currencySymbol(this.settings.project.currency);
      },
      prettyRateDate() {
        return prettyDates(this.currency_rate_date);
      },
    },
    data() {
      return {
        currency_rate_date: this.settings.project.currency_rate_date,
        currency_rates_info: this.settings.project.currency_rates_info,
        currency_rates: this.settings.project.currency_rates,
      };
    },
    methods: {
      ...mapActions({
        openConfirm: 'confirmModal/openModal',
        showAlert: 'alert/showAlert',
      }),
      handleConversionUpdate() {
        this.openConfirm({
          title: this.$i18n.t('Update conversion rates?'),
          description: `${this.$i18n.t('You now have conversion rates set for')} ${
            this.prettyRateDate
          }.`,
          actionText: this.$i18n.t('Update rates'),
          component: RatesUpdate,
          data: {
            date: this.currency_rate_date || currentDate(),
            projectId: this.settings.project.id,
          },
          action: async () => {
            try {
              if (this.context.data.rates) {
                this.currency_rate_date = this.context.data.date;
                this.currency_rates = this.context.data.rates;

                await factory('projects').patchInPath(this.settings.project.id, {
                  currency_rate_date: this.context.data.date,
                  currency_rates: this.context.data.rates,
                });
              }
            } catch (e) {
              this.showAlert({
                type: 'error',
                text: parseError(e),
              });
              console.error(e);
            }
          },
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .three-columns-form-row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-column-gap: 20px;
  }
</style>
